/**
 * Productions tab — mobile-first list of all productions across channels.
 * Pull-to-refresh (browser default). FAB to create new. Tap to open detail.
 *
 * Designed for Flip 7 inner screen (6.7"). Tap targets ≥44px. One-handed
 * reach: list scrolls in the middle, FAB sits bottom-right.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus, RefreshCw, Sparkles } from 'lucide-react';
import { api, type Production, type Channel } from '../lib/api';
import { BottomNav } from '../components/BottomNav';

interface EpisodeSuggestion {
  title: string;
  hook: string;
  rationale: string;
  seoFraming: string;
  confidence: number | null;
}

type ProductionRow = Production & {
  channel?: { name: string };
  updatedAt?: number;
};

export function Productions() {
  const [rows, setRows] = useState<ProductionRow[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const [productions, channelList] = await Promise.all([
        api.get<ProductionRow[]>('/productions'),
        api.get<Channel[]>('/channels'),
      ]);
      // Newest first.
      productions.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
      setRows(productions);
      setChannels(channelList);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-2">
        <h1 className="font-serif text-xl flex-1">Productions</h1>
        <button onClick={() => setShowSuggest(true)} className="tap p-2" aria-label="Suggest episodes">
          <Sparkles className="h-5 w-5" />
        </button>
        <button onClick={refresh} className="tap p-2" aria-label="Refresh">
          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      <main className="flex-1 px-4 py-3 space-y-2 pb-24">
        {err && (
          <div className="card border-danger/50 text-danger text-sm">{err}</div>
        )}
        {!loading && rows.length === 0 && (
          <p className="text-muted text-sm text-center py-12">
            No productions yet. Tap + to start one.
          </p>
        )}
        {rows.map((r) => (
          <ProductionCard key={r.id} row={r} />
        ))}
      </main>

      <button
        onClick={() => setShowAdd(true)}
        className="fixed bottom-24 right-6 tap rounded-full bg-accent text-bg shadow-lg p-4 hover:bg-accentSoft"
        aria-label="New production"
      >
        <Plus className="h-6 w-6" />
      </button>

      <BottomNav active="productions" />

      {showAdd && (
        <NewProductionSheet
          channels={channels}
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            void refresh();
          }}
        />
      )}

      {showSuggest && (
        <SuggestSheet
          channels={channels}
          onClose={() => setShowSuggest(false)}
          onCreated={() => { void refresh(); }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Episode suggester sheet — Groq proposes new episode topics from the
// channel slate + YT performance. One-tap create. ~£0.001/refresh.
// ----------------------------------------------------------------------
function SuggestSheet({
  channels,
  onClose,
  onCreated,
}: {
  channels: Channel[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [channelId, setChannelId] = useState(channels[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<EpisodeSuggestion[] | null>(null);
  const [creating, setCreating] = useState<string | null>(null);
  const [created, setCreated] = useState<Set<string>>(new Set());

  async function suggest() {
    if (!channelId) { setErr('Pick a channel'); return; }
    setBusy(true);
    setErr(null);
    setSuggestions(null);
    try {
      const r = await api.get<{ suggestions: EpisodeSuggestion[] }>(`/channels/${channelId}/episode-suggestions`);
      setSuggestions(r.suggestions ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function createFrom(s: EpisodeSuggestion) {
    setCreating(s.title);
    setErr(null);
    try {
      await api.post('/productions', { topic: s.title, workingTitle: s.title, channelId });
      setCreated((prev) => new Set(prev).add(s.title));
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center" onClick={onClose}>
      <div
        className="w-full max-w-md card rounded-t-2xl rounded-b-none mb-0 space-y-3 m-0 max-h-[85vh] overflow-y-auto"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-lg">What should I make next?</h2>
        <p className="text-xs text-muted">Groq reads your slate + YouTube performance and suggests new episodes. ~£0.001.</p>
        <div className="flex gap-2">
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className="input py-2 flex-1 text-sm"
          >
            {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={suggest} disabled={busy} className="btn-primary px-4 py-2 text-sm">
            {busy ? '…' : 'Suggest'}
          </button>
        </div>

        {err && <p className="text-sm text-danger break-words">{err}</p>}

        {suggestions && suggestions.length > 0 && (
          <ul className="space-y-2">
            {suggestions.map((s) => {
              const isCreated = created.has(s.title);
              return (
                <li key={s.title} className="rounded-md border border-border p-3">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-sm font-medium leading-tight">{s.title}</span>
                        {s.confidence != null && (
                          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${
                            s.confidence >= 0.8 ? 'bg-emerald-950/60 text-emerald-300'
                              : s.confidence >= 0.65 ? 'bg-amber-950/60 text-amber-300'
                              : 'bg-border text-muted'
                          }`}>{Math.round(s.confidence * 100)}%</span>
                        )}
                      </div>
                      {s.hook && <p className="mt-1 text-xs text-muted line-clamp-2">{s.hook}</p>}
                    </div>
                    <button
                      onClick={() => createFrom(s)}
                      disabled={isCreated || creating === s.title}
                      className={`shrink-0 rounded-md px-3 py-2 text-xs font-medium ${
                        isCreated ? 'bg-emerald-900/40 text-emerald-300' : 'btn-primary'
                      }`}
                    >
                      {isCreated ? '✓' : creating === s.title ? '…' : 'Create'}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <button onClick={onClose} className="btn-ghost w-full py-3">Close</button>
      </div>
    </div>
  );
}

function ProductionCard({ row }: { row: ProductionRow }) {
  return (
    <Link to={`/productions/${row.id}`} className="card flex gap-3 active:bg-border/30">
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-sm leading-tight line-clamp-2">
          {row.topic}
        </h3>
        <div className="flex items-center gap-2 mt-2 text-xs text-muted">
          <StateBadge state={row.state} />
          {row.channel?.name && <span>{row.channel.name}</span>}
          {row.updatedAt && <span>{relTime(row.updatedAt)}</span>}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted self-center shrink-0" />
    </Link>
  );
}

function StateBadge({ state }: { state: string }) {
  // Group states by category for the badge colour.
  const phase =
    state === 'created' || state === 'sources_uploaded'
      ? 'draft'
      : state.startsWith('factsheet')
        ? 'research'
        : state.startsWith('outline') || state.startsWith('script')
          ? 'writing'
          : state.startsWith('preprocess') || state.startsWith('tts') || state.startsWith('audio')
            ? 'audio'
            : state.startsWith('assembl')
              ? 'assembly'
              : state === 'ready_to_upload' || state === 'uploaded'
                ? 'done'
                : 'other';
  const cls =
    phase === 'draft'
      ? 'bg-muted/20 text-muted'
      : phase === 'research'
        ? 'bg-accent/20 text-accent'
        : phase === 'writing'
          ? 'bg-blue-500/20 text-blue-300'
          : phase === 'audio'
            ? 'bg-amber-500/20 text-amber-300'
            : phase === 'assembly'
              ? 'bg-purple-500/20 text-purple-300'
              : phase === 'done'
                ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-muted/20 text-muted';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono ${cls}`}>
      {state.replace(/_/g, ' ')}
    </span>
  );
}

function NewProductionSheet({
  channels,
  onClose,
  onCreated,
}: {
  channels: Channel[];
  onClose: () => void;
  onCreated: (production: Production) => void;
}) {
  const [topic, setTopic] = useState('');
  const [channelId, setChannelId] = useState(channels[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!topic.trim() || !channelId) return;
    setBusy(true);
    try {
      const r = await api.post<Production>('/productions', {
        topic: topic.trim(),
        workingTitle: topic.trim(),
        channelId,
      });
      onCreated(r);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md card rounded-t-2xl rounded-b-none mb-0 space-y-3 m-0"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-lg">New production</h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-muted block mb-1">Channel</label>
            <select
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              className="input py-3"
            >
              {channels.length === 0 && <option value="">No channels</option>}
              {channels.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted block mb-1">Title / topic</label>
            <input
              autoFocus
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Rosslyn Chapel"
              className="input py-3"
            />
          </div>
          {err && <p className="text-sm text-danger break-all">{err}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 py-3">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !topic.trim() || !channelId}
              className="btn-primary flex-1 py-3"
            >
              {busy ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function relTime(unix: number): string {
  const ms = Date.now() - unix * 1000;
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(unix * 1000).toLocaleDateString();
}
